import React, { useEffect, useRef, useState } from 'react';
import loadable from '@loadable/component';
import classNames from 'classnames';

import appSettings from '../../../config/settings';
import { useConfiguration } from '../../../context/configurationContext';
import { useRouteConfiguration } from '../../../context/routeConfigurationContext';

import { pickBy } from '../../../util/common';
import { FormattedMessage, useIntl } from '../../../util/reactIntl';
import {
  clearPendingBooking,
  readPendingBooking,
  shouldClearAfterSignIn,
  shouldClearOnDialogDismissed,
} from '../../../util/pendingBooking';
import {
  parse,
  pathWithLoginModal,
  pathWithoutLoginModal,
  stringify,
} from '../../../util/urlHelpers';
import { matchPathname, pathByRouteName } from '../../../util/routes';
import {
  Avatar,
  Button,
  IconArrowHead,
  NamedLink,
  LimitedAccessBanner,
  LinkedLogo,
  Modal,
  ModalMissingInformation,
} from '../../../components';
import UnifiedSearch from '../../../components/UnifiedSearch/UnifiedSearch';

import MenuIcon from './MenuIcon';
import TopbarMobileMenu from './TopbarMobileMenu/TopbarMobileMenu';
import LoginModal from './LoginModal/LoginModal';
import ProfileIcon from './ProfileIcon';

import css from './Topbar.module.css';
import { getCurrentUserTypeRoles, showCreateListingLinkForUser } from '../../../util/userHelpers';

const TopbarDesktop = loadable(() => import('./TopbarDesktop/TopbarDesktop'));

const AUTH_ROUTE_NAMES = ['LoginPage', 'SignupPage', 'SignupForUserTypePage', 'ConfirmPage'];

const MAX_MOBILE_SCREEN_WIDTH = 1024;

const MOBILE_MENU_BUTTON_ID = 'mobileMenuButton';
const UNIFIED_SEARCH_PAGES = [
  'SearchPage',
  'SearchPageWithListingType',
  'CityLandingPage',
  'CityLandingTypePage',
  'AirportLoungeLandingPage',
  'ListingPage',
  'ListingPageCanonical',
  'ListingPageVariant',
  'ListingBasePage',
  'ProfilePage',
  'ProfilePageVariant',
  'ProfileBasePage',
  'CMSPage',
  'FAQLandingPage',
  'SupportPage',
  'NotFoundPage',
];
const UNIFIED_RESULTS_PAGES = [
  'SearchPage',
  'SearchPageWithListingType',
  'CityLandingPage',
  'CityLandingTypePage',
];
const MOBILE_ACCOUNT_BUTTON_ID = 'mobileAccountButton';

const redirectToURLWithModalState = (history, location, modalStateParam) => {
  const { pathname, search, state } = location;
  const searchString = `?${stringify({ [modalStateParam]: 'open', ...parse(search) })}`;
  history.push(`${pathname}${searchString}`, state);
};

const redirectToURLWithoutModalState = (history, location, modalStateParam) => {
  const { pathname, search, state } = location;
  const queryParams = pickBy(parse(search), (v, k) => {
    return k !== modalStateParam;
  });
  const stringified = stringify(queryParams);
  const searchString = stringified ? `?${stringified}` : '';
  history.push(`${pathname}${searchString}`, state);
};

const isPrimary = o => o.group === 'primary';
const isSecondary = o => o.group === 'secondary';
const compareGroups = (a, b) => {
  const isAHigherGroupThanB = isPrimary(a) && isSecondary(b);
  const isALesserGroupThanB = isSecondary(a) && isPrimary(b);
  // Note: sort order is stable in JS
  return isAHigherGroupThanB ? -1 : isALesserGroupThanB ? 1 : 0;
};
// Returns links in order where primary links are returned first
const sortCustomLinks = customLinks => {
  const links = Array.isArray(customLinks) ? [...customLinks] : [];
  return links.sort(compareGroups);
};

// Resolves in-app links against route configuration
const getResolvedCustomLinks = (customLinks, routeConfiguration) => {
  const links = Array.isArray(customLinks) ? customLinks : [];
  return links.map(linkConfig => {
    const { type, href } = linkConfig;
    const isInternalLink = type === 'internal' || href.charAt(0) === '/';
    if (isInternalLink) {
      // Internal link
      try {
        const testURL = new URL('http://my.marketplace.com' + href);
        const matchedRoutes = matchPathname(testURL.pathname, routeConfiguration);
        if (matchedRoutes.length > 0) {
          const found = matchedRoutes[0];
          const to = { search: testURL.search, hash: testURL.hash };
          return {
            ...linkConfig,
            route: {
              name: found.route?.name,
              params: found.params,
              to,
            },
          };
        }
      } catch (e) {
        return linkConfig;
      }
    }
    return linkConfig;
  });
};

const isCMSPage = found =>
  found.route?.name === 'CMSPage' ? `CMSPage:${found.params?.pageId}` : null;
const isInboxPage = found =>
  found.route?.name === 'InboxPage' ? `InboxPage:${found.params?.tab}` : null;
// Find the name of the current route/pathname.
// It's used as handle for currentPage check.
const getResolvedCurrentPage = (location, routeConfiguration) => {
  const matchedRoutes = matchPathname(location.pathname, routeConfiguration);
  if (matchedRoutes.length > 0) {
    const found = matchedRoutes[0];
    const cmsPageName = isCMSPage(found);
    const inboxPageName = isInboxPage(found);
    return cmsPageName ? cmsPageName : inboxPageName ? inboxPageName : `${found.route?.name}`;
  }
};

const GenericError = props => {
  const { show } = props;
  const classes = classNames(css.genericError, {
    [css.genericErrorVisible]: show,
  });
  return show ? (
    <div className={classes} role="alert">
      <div className={css.genericErrorContent}>
        <p className={css.genericErrorText}>
          <FormattedMessage id="Topbar.genericError" />
        </p>
      </div>
    </div>
  ) : null;
};

const TopbarComponent = props => {
  const {
    className,
    rootClassName,
    desktopClassName,
    mobileRootClassName,
    mobileClassName,
    isAuthenticated,
    isLoggedInAs,
    authScopes = [],
    authInProgress,
    currentUser,
    currentUserHasListings,
    currentUserHasOrders,
    currentPage,
    notificationCount = 0,
    intl,
    history,
    location,
    onManageDisableScrolling,
    onResendVerificationEmail,
    sendVerificationEmailInProgress,
    sendVerificationEmailError,
    showGenericError,
    config,
    routeConfiguration,
  } = props;

  // SSR-safe layout detection: the server renders the mobile-first default,
  // so the first client render must too (otherwise React hydration fails with
  // #418/#423 and falls back to a full client re-render on desktop viewports).
  // The real viewport is applied right after mount and on viewport changes.
  const [isMobileLayout, setIsMobileLayout] = useState(true);
  // Same rule as isMobileLayout above, for a different reason: SSR always renders logged out, so
  // the avatar must not appear in the first client render either. Showing it only after mount keeps
  // the hydrated markup identical to the server's.
  const [mounted, setMounted] = useState(false);
  useEffect(() => {
    setMounted(true);
  }, []);
  useEffect(() => {
    if (!window?.matchMedia) {
      return undefined;
    }
    const mobileMq = window.matchMedia(`(max-width: ${MAX_MOBILE_SCREEN_WIDTH}px)`);
    const update = () => setIsMobileLayout(mobileMq.matches);
    update();
    if (mobileMq.addEventListener) {
      mobileMq.addEventListener('change', update);
      return () => mobileMq.removeEventListener('change', update);
    }
    return undefined;
  }, []);
  const isDesktopLayout = !isMobileLayout;

  const handleLogout = () => {
    const { onLogout, history, routeConfiguration } = props;
    onLogout().then(() => {
      const path = pathByRouteName('LandingPage', routeConfiguration);

      // In production we ensure that data is really lost,
      // but in development mode we use stored values for debugging
      if (appSettings.dev) {
        history.push(path);
      } else if (typeof window !== 'undefined') {
        window.location = path;
      }
    });
  };

  const showCreateListingsLink = showCreateListingLinkForUser(config, currentUser);
  const { customer: isCustomer, provider: isProvider } = getCurrentUserTypeRoles(
    config,
    currentUser
  );

  /**
   * Determine which tab to use in the inbox link:
   * - if only provider role – sales
   * - if only customer role – orders
   * - if both roles – determine by currentUserHasListings value
   */
  const topbarInboxTab = !isCustomer
    ? 'sales'
    : !isProvider
    ? 'orders'
    : currentUserHasListings
    ? 'sales'
    : 'orders';

  const { mobilemenu, authmodal } = parse(location.search);

  // Custom links are sorted so that group="primary" are always at the beginning of the list.
  const sortedCustomLinks = sortCustomLinks(config.topbar?.customLinks);
  const customLinks = getResolvedCustomLinks(sortedCustomLinks, routeConfiguration);
  const resolvedCurrentPage = currentPage || getResolvedCurrentPage(location, routeConfiguration);

  const notificationDot = notificationCount > 0 ? <div className={css.notificationDot} /> : null;

  const isMobileMenuOpen = isMobileLayout && mobilemenu === 'open';
  // Login modal state lives in the URL like the other topbar modals, so the browser back button
  // closes it and the state survives a reload. Unlike the mobile menu it is not layout-gated —
  // desktop and mobile open the same dialog.
  // `mounted` for the same reason the Topbar's own account menu below uses it: SSR renders with
  // isAuthenticated false (the server store never dispatches authInfo) while src/index.js awaits
  // authInfo() before hydrateRoot, so without it a signed-in visitor loading an ?authmodal=open URL
  // gets the whole dialog painted into the server HTML and then torn out at hydration — React
  // #418/#423 and a discarded SSR tree. The booking CTA made that URL shape common: it leaves
  // ?authmodal=open on the history entry, so any reload or shared link hits it.
  const isLoginModalOpen = mounted && authmodal === 'open' && !isAuthenticated;

  // /login, /signup and the confirm step are part of the sign-in journey, not somewhere the
  // visitor wandered off to — the cleanups below have to be able to tell those apart.
  const currentRouteName = matchPathname(location.pathname, routeConfiguration)[0]?.route?.name;
  const isOnAuthPage = AUTH_ROUTE_NAMES.includes(currentRouteName);
  // Opening from the mobile menu has to close that menu first, otherwise the two modals stack.
  // A successful login flips isAuthenticated but leaves ?authmodal=open in the URL. Strip it, or
  // the stale param sits in the address bar and re-opens the dialog if the user later logs out on
  // that same URL.
  // Dismissing the dialog without signing in means the visitor said no to the account, so a booking
  // they were sent here to finish stops being pending. Watching the param rather than hooking
  // onClose covers Escape as well as the close button.
  //
  // It does not cover the browser Back button: every entry point opens the dialog with
  // history.replace, so Back leaves the listing entirely and unmounts this Topbar before the effect
  // can see the transition. The effect below is what catches that case — a sign-in that then
  // happens anywhere other than the listing drops the intent — and beyond it the 15-minute window
  // bounds how long a forgotten intent can be replayed.
  const wasLoginModalOpenRef = useRef(false);
  useEffect(() => {
    const isOpen = authmodal === 'open';
    if (wasLoginModalOpenRef.current && !isOpen && !isAuthenticated) {
      // Only a dismissal if the visitor is still on the listing the dialog was opened from.
      // Clicking "Jetzt registrieren" also drops the authmodal param — it navigates to /signup —
      // and reading that as "they said no to the account" deleted the intent of every visitor who
      // took the one path the dialog exists to offer. Signing in resolves in place and keeps the
      // pathname, which is why password login worked and creating an account never did.
      if (
        shouldClearOnDialogDismissed({ pending: readPendingBooking(), pathname: location.pathname })
      ) {
        clearPendingBooking();
      }
    }
    wasLoginModalOpenRef.current = isOpen;
  }, [authmodal, isAuthenticated, location.pathname]);

  // A sign-in that completed somewhere other than the listing the booking was for was not the one
  // the booking CTA asked for. Forget the intent, or returning to that listing later navigates the
  // visitor into checkout with no click of theirs — the listing-id check in
  // usePendingBookingResume only rules out resuming on the *wrong* listing, not resuming a booking
  // the visitor has moved on from.
  //
  // Deliberately NOT gated on `authmodal === 'open'`: a social login is a full round-trip that
  // returns to `pathWithoutLoginModal(location)`, which by construction has no authmodal param — so
  // the one flow most likely to land the visitor on a different page would have been the one flow
  // this never ran on.
  useEffect(() => {
    // Not while the visitor is still standing on an auth page. Signing up completes ON /signup, and
    // AuthenticationPage only redirects back to the listing on the render after that — so this
    // would fire in between and throw away the booking the account was just created for.
    if (!isAuthenticated || isOnAuthPage) {
      return;
    }
    const pending = readPendingBooking();
    if (shouldClearAfterSignIn({ pending, pathname: location.pathname, isOnAuthPage })) {
      clearPendingBooking();
    }
  }, [isAuthenticated, isOnAuthPage, location.pathname]);

  useEffect(() => {
    if (isAuthenticated && authmodal === 'open') {
      history.replace(pathWithoutLoginModal(location), location.state);
    }
  }, [isAuthenticated, authmodal, history, location]);

  const handleOpenLoginModal = () => {
    // replace, not push: opening the dialog is not a place in the history. With push, closing it
    // and pressing Back walked the visitor through open/closed states instead of leaving the page,
    // and the post-login cleanup below added a third entry on top.
    history.replace(pathWithLoginModal(location));
  };

  const mobileMenu = (
    <TopbarMobileMenu
      isAuthenticated={isAuthenticated}
      currentUser={currentUser}
      onLogout={handleLogout}
      notificationCount={notificationCount}
      currentPage={resolvedCurrentPage}
      customLinks={customLinks}
      showCreateListingsLink={showCreateListingsLink}
      inboxTab={topbarInboxTab}
      onOpenLoginModal={handleOpenLoginModal}
    />
  );

  const classes = classNames(rootClassName || css.root, className);

  // The hosted `searchBar.display` (prod: onlySearchPage) does not decide: the pill is on the pages
  // where people browse (spec §4.2) — not on the landing page, whose hero carries the same element,
  // and not in checkout, sign-in, the listing wizard or the account pages, where one tap would lead
  // away from an unfinished form.
  const showSearchForm = UNIFIED_SEARCH_PAGES.includes(resolvedCurrentPage);
  // Pages whose results the search describes: on phones the pill gets its own row there; elsewhere
  // a magnifier in the bar opens the same dialog without pushing the page content down.
  const isResultsPage = UNIFIED_RESULTS_PAGES.includes(resolvedCurrentPage);
  const showUnifiedSearchRow = showSearchForm && isResultsPage && !isDesktopLayout;

  const mobileSearchButtonMaybe = (
    <div className={css.searchMenu}>
      {showSearchForm && !isResultsPage && !isDesktopLayout ? (
        <UnifiedSearch placement="topbar" trigger="icon" />
      ) : null}
    </div>
  );

  const handleSkipToMainContent = e => {
    e.preventDefault();
    const mainContent = document.getElementById('main-content');
    if (mainContent) {
      mainContent.scrollIntoView({ behavior: 'smooth', block: 'start' });
      // Focus the main content for screen readers
      mainContent.setAttribute('tabindex', '-1');
      mainContent.focus();
      // Remove tabindex after blur to avoid tabbing into it later
      mainContent.addEventListener(
        'blur',
        () => {
          mainContent.removeAttribute('tabindex');
        },
        { once: true }
      );
    }
  };

  return (
    <div className={classes}>
      <Button onClick={handleSkipToMainContent} className={css.skipToMainContent}>
        <FormattedMessage id="Topbar.skipToMainContent" />
        <IconArrowHead direction="right" size="small" rootClassName={css.skiptoMainArrow} />
      </Button>
      <LimitedAccessBanner
        isAuthenticated={isAuthenticated}
        isLoggedInAs={isLoggedInAs}
        authScopes={authScopes}
        currentUser={currentUser}
        onLogout={handleLogout}
        currentPage={resolvedCurrentPage}
      />
      <nav className={classNames(mobileRootClassName || css.container, mobileClassName)}>
        <Button
          id={MOBILE_MENU_BUTTON_ID}
          rootClassName={css.menu}
          onClick={() => redirectToURLWithModalState(history, location, 'mobilemenu')}
          title={intl.formatMessage({ id: 'Topbar.menuIcon' })}
        >
          <MenuIcon
            className={css.menuIcon}
            ariaLabel={intl.formatMessage({ id: 'Topbar.menuIcon' })}
          />
          {notificationDot}
        </Button>
        <LinkedLogo
          id="logo-topbar-mobile"
          className={css.mobileLogo}
          layout={'mobile'}
          alt={intl.formatMessage({ id: 'Topbar.logoIcon' })}
          linkToExternalSite={config?.topbar?.logoLink}
        />
        {mobileSearchButtonMaybe}
        {/* Account entry on mobile, mirroring the desktop header. The right slot was empty on any
            page that hides the topbar search (the landing page hides it, because the hero carries
            its own), so a logged-out visitor on mobile had no way in except the hamburger.
            The two states render DIFFERENT markup — <a> via NamedLink versus <button> — so what
            keeps this hydration-safe is the `mounted` guard alone: SSR and the first client render
            both take the logged-out branch, and the avatar only appears on the second render. */}
        {mounted && isAuthenticated ? (
          // Logged in: the real avatar, linking straight to the profile. A NamedLink rather than a
          // button so it behaves like a link (new tab, middle-click, copy address).
          <NamedLink
            name="ProfileSettingsPage"
            className={css.profileButton}
            title={intl.formatMessage({ id: 'Topbar.profileLink' })}
          >
            <Avatar className={css.profileAvatar} user={currentUser} disableProfileLink />
          </NamedLink>
        ) : (
          <Button
            id={MOBILE_ACCOUNT_BUTTON_ID}
            rootClassName={css.profileButton}
            onClick={handleOpenLoginModal}
            title={intl.formatMessage({ id: 'Topbar.accountIcon' })}
          >
            <ProfileIcon className={css.profileIcon} size={20} />
          </Button>
        )}
      </nav>
      {showUnifiedSearchRow ? (
        <div className={css.unifiedSearchRow}>
          <UnifiedSearch placement="topbar" />
        </div>
      ) : null}
      {isDesktopLayout ? (
        <div className={css.desktop}>
          <TopbarDesktop
            className={desktopClassName}
            currentUserHasListings={currentUserHasListings}
            currentUser={currentUser}
            currentPage={resolvedCurrentPage}
            intl={intl}
            isAuthenticated={isAuthenticated}
            notificationCount={notificationCount}
            onLogout={handleLogout}
            config={config}
            customLinks={customLinks}
            showSearchForm={showSearchForm}
            showCreateListingsLink={showCreateListingsLink}
            inboxTab={topbarInboxTab}
            onOpenLoginModal={handleOpenLoginModal}
          />
        </div>
      ) : null}
      <Modal
        id="TopbarMobileMenu"
        containerClassName={css.modalContainer}
        isOpen={isMobileMenuOpen}
        onClose={() => redirectToURLWithoutModalState(history, location, 'mobilemenu')}
        usePortal
        onManageDisableScrolling={onManageDisableScrolling}
        focusElementId={MOBILE_MENU_BUTTON_ID}
      >
        {authInProgress ? null : mobileMenu}
      </Modal>
      <LoginModal
        // Which control opened the dialog depends on the layout, so focus has to return to the one
        // that actually exists: the desktop guest menu lives in TopbarDesktop, which is not rendered
        // on mobile at all. Passing the desktop id unconditionally dropped focus on <body>.
        focusElementId={isMobileLayout ? MOBILE_ACCOUNT_BUTTON_ID : 'guest-menu-label'}
        isOpen={isLoginModalOpen}
        // Where a social login returns the visitor to. Deliberately the current page rather than
        // the landing page: the whole reason this dialog exists is that being sent somewhere else
        // costs the visitor the browsing context they had just built up.
        from={pathWithoutLoginModal(location)}
        // history.location for the same reason OrderPanel uses it: Escape closes this dialog and
        // the stacked mobile order panel together, and the second handler must not undo the first.
        onClose={() =>
          history.replace(pathWithoutLoginModal(history.location), history.location.state)
        }
        onManageDisableScrolling={onManageDisableScrolling}
      />
      <ModalMissingInformation
        id="MissingInformationReminder"
        containerClassName={css.missingInformationModal}
        currentUser={currentUser}
        currentUserHasListings={currentUserHasListings}
        currentUserHasOrders={currentUserHasOrders}
        location={location}
        onManageDisableScrolling={onManageDisableScrolling}
        onResendVerificationEmail={onResendVerificationEmail}
        sendVerificationEmailInProgress={sendVerificationEmailInProgress}
        sendVerificationEmailError={sendVerificationEmailError}
      />

      <GenericError show={showGenericError} />
    </div>
  );
};

/**
 * Topbar containing logo, main search and navigation links.
 *
 * @component
 * @param {Object} props
 * @param {string?} props.className add more style rules in addition to components own css.root
 * @param {string?} props.rootClassName overwrite components own css.root
 * @param {Object} props.desktopClassName add more style rules for TopbarDesktop
 * @param {Object} props.mobileRootClassName overwrite mobile layout root classes
 * @param {Object} props.mobileClassName add more style rules for mobile layout
 * @param {boolean} props.isAuthenticated
 * @param {boolean} props.isLoggedInAs
 * @param {Object} props.currentUser
 * @param {boolean} props.currentUserHasListings
 * @param {boolean} props.currentUserHasOrders
 * @param {string} props.currentPage
 * @param {number} props.notificationCount
 * @param {Function} props.onLogout
 * @param {Function} props.onManageDisableScrolling
 * @param {Function} props.onResendVerificationEmail
 * @param {Object} props.sendVerificationEmailInProgress
 * @param {Object} props.sendVerificationEmailError
 * @param {boolean} props.showGenericError
 * @param {Object} props.history
 * @param {Function} props.history.push
 * @param {Object} props.location
 * @param {string} props.location.search '?foo=bar'
 * @returns {JSX.Element} topbar component
 */
const Topbar = props => {
  const config = useConfiguration();
  const routeConfiguration = useRouteConfiguration();
  const intl = useIntl();
  return (
    <TopbarComponent
      config={config}
      routeConfiguration={routeConfiguration}
      intl={intl}
      {...props}
    />
  );
};

export default Topbar;
